import { DateRangePicker, type IsoRange } from "@/components/ui/date-range-picker";
// src/features/links/LinksFilterBar.tsx
// IR-1047 Phase 3.5 — Join Links filter bar, reworked to the OPS convention
// (Rotem): every multi-value filter is a dropdown with multi-select + inner
// Apply/Clear; the date range is one standard picker (not two date fields);
// the bar is sticky; Reset sits on the right next to the Platform filter.
import type React from "react";
import type { ReactNode } from "react";
import { MultiSelectFilter } from "./MultiSelectFilter";
import type { LinksFilters } from "./api";
import type { Platform } from "./types";

interface Props {
  value: LinksFilters;
  onChange: (next: LinksFilters) => void;
  /** Classification filter options (data-driven facet from the links response). */
  classificationOptions?: string[];
  /** IR-1047 (Sean): finder-avatar options ({ value: avatar id as string, label: name }). */
  avatarFinderOptions?: { value: string; label: string }[];
  /** IR-1047 (Rotem 2026-06-15): originating source-group options ({ value: entity id as string, label: name }). */
  sourceGroupOptions?: { value: string; label: string }[];
  /**
   * IR-1386 r3 (Rotem) — rendered to the RIGHT of the filter grid, on the same
   * line. Currently the "By type" pie. A slot rather than the chart itself so
   * the filter bar stays a filter bar and doesn't take a chart dependency.
   */
  aside?: ReactNode;
}

const PLATFORMS: { value: string; label: string }[] = [
  { value: "tg", label: "Telegram" },
  { value: "wa", label: "WhatsApp" },
  { value: "signal", label: "Signal" },
];

// IR-1047 (Rotem 45432): risk-score bands — mirror the backend _RISK_BANDS.
const RISK_BANDS: { value: string; label: string }[] = [
  { value: "low", label: "Low (0–33)" },
  { value: "medium", label: "Medium (34–66)" },
  { value: "high", label: "High (67–100)" },
];

// IR-1047 — target kinds (what the link points AT). Mirrors the backend KINDS;
// 'unknown' is intentionally omitted (nothing to filter for — it's the default).
const TARGET_KINDS: { value: string; label: string }[] = [
  { value: "public_group", label: "Public group" },
  { value: "private_group", label: "Private group" },
  { value: "public_channel", label: "Public channel" },
  { value: "private_channel", label: "Private channel" },
  { value: "user", label: "User" },
  { value: "bot", label: "Bot" },
];

const OP_STATUSES: { value: string; label: string }[] = [
  { value: "new", label: "New" },
  { value: "in_review", label: "In Review" },
  { value: "waiting4approval", label: "Waiting for Approval" },
  { value: "in_coverage", label: "In Coverage" },
  { value: "rejected", label: "Rejected" },
  { value: "broken", label: "Broken" },
  { value: "obsolete", label: "Obsolete" },
  { value: "failed", label: "Failed" },
];

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex min-w-[12rem] flex-col gap-1.5">
      <span className="text-xs text-[var(--iris-fg-3)]">{label}</span>
      {children}
    </div>
  );
}

export function LinksFilterBar({
  value,
  onChange,
  classificationOptions = [],
  avatarFinderOptions = [],
  sourceGroupOptions = [],
  aside,
}: Props) {
  // Generic array-filter set: write `key` to the truthy array or drop it when
  // empty (platform / operational_status / risk_band all emit repeated params).
  const setArray = (
    key: "platform" | "operational_status" | "risk_band" | "target_kind",
    next: string[],
  ) => {
    const rest = { ...value } as Record<string, unknown>;
    delete rest[key];
    if (next.length) rest[key] = key === "platform" ? (next as Platform[]) : next;
    onChange(rest as LinksFilters);
  };

  // IR-1047 (Sean): finder avatar — multi-select of numeric avatar ids. The
  // MultiSelectFilter works in strings; the filter field is typed number[], so
  // map back to numbers on apply (and to strings for the selected prop).
  const setAvatarFinder = (next: string[]) => {
    const { avatar_finder_id: _f, ...rest } = value;
    onChange(next.length ? { ...rest, avatar_finder_id: next.map(Number) } : rest);
  };

  // IR-1047 (Rotem 2026-06-15): originating source group — multi-select of
  // numeric entity ids (string↔number, same convention as the finder filter).
  const setSourceGroup = (next: string[]) => {
    const { source_group_id: _g, ...rest } = value;
    onChange(next.length ? { ...rest, source_group_id: next.map(Number) } : rest);
  };

  // Classification is a single CSV param server-side (params.get), so the
  // multi-select array is joined to/ split from a comma string.
  const setClassification = (next: string[]) => {
    const { classification: _c, ...rest } = value;
    onChange(next.length ? { ...rest, classification: next.join(",") } : rest);
  };

  const setRange = (r: IsoRange) => {
    const { date_from: _df, date_to: _dt, ...rest } = value;
    onChange({
      ...rest,
      ...(r.from ? { date_from: r.from } : {}),
      ...(r.to ? { date_to: r.to } : {}),
    });
  };

  const setApproval = (raw: string) => {
    const { approval_required: _a, ...rest } = value;
    onChange(
      raw === "true" || raw === "false" ? { ...rest, approval_required: raw === "true" } : rest,
    );
  };

  const approvalValue =
    value.approval_required === true ? "true" : value.approval_required === false ? "false" : "";

  return (
    <div className="iris-card sticky top-0 z-10 flex items-stretch gap-3 bg-[var(--iris-bg-1)] p-4">
      {/* IR-1386 r3 (Rotem): "2 rows of filters and a box for the pie". The
          filter grid takes the space it needs and wraps at 2 rows; `aside`
          (the type pie) sits on the SAME line rather than pushing the table
          further down a screen an analyst already scrolls. */}
      <div className="flex flex-1 flex-wrap content-start items-end gap-3">
        <Field label="Platform">
          <MultiSelectFilter
            label="Platform"
            options={PLATFORMS}
            selected={value.platform ?? []}
            onApply={(next) => setArray("platform", next)}
          />
        </Field>

        <Field label="Operational status">
          <MultiSelectFilter
            label="Operational status"
            options={OP_STATUSES}
            selected={value.operational_status ?? []}
            onApply={(next) => setArray("operational_status", next)}
          />
        </Field>

        <Field label="Type">
          <MultiSelectFilter
            label="Type"
            options={TARGET_KINDS}
            selected={value.target_kind ?? []}
            onApply={(next) => setArray("target_kind", next)}
          />
        </Field>

        <Field label="Risk score">
          <MultiSelectFilter
            label="Risk score"
            options={RISK_BANDS}
            selected={value.risk_band ?? []}
            onApply={(next) => setArray("risk_band", next)}
          />
        </Field>

        <Field label="Classification">
          <MultiSelectFilter
            label="Classification"
            options={classificationOptions.map((c) => ({ value: c, label: c }))}
            selected={value.classification ? value.classification.split(",") : []}
            onApply={setClassification}
          />
        </Field>

        <Field label="Avatar (finder)">
          <MultiSelectFilter
            label="Avatar (finder)"
            options={avatarFinderOptions}
            selected={(value.avatar_finder_id ?? []).map(String)}
            onApply={setAvatarFinder}
            // IR-1386 r4 (Rotem): "Avatar - should have a search row - it
            // doesn't help to manually scroll tens of Avatars". The fleet is
            // ~57 avatars and this was the only filter on the bar without the
            // type-to-filter box that Group has had since IR-1047.
            searchable
          />
        </Field>

        <Field label="Group">
          <MultiSelectFilter
            label="Group"
            options={sourceGroupOptions}
            selected={(value.source_group_id ?? []).map(String)}
            onApply={setSourceGroup}
            searchable
          />
        </Field>

        <Field label="Approval">
          <select
            aria-label="Approval"
            value={approvalValue}
            onChange={(e) => setApproval(e.target.value)}
            className="iris-input w-full text-sm"
          >
            <option value="">Any</option>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
        </Field>

        <Field label="First seen">
          <DateRangePicker
            label="Any time"
            value={{ from: value.date_from, to: value.date_to }}
            onApply={setRange}
          />
        </Field>

        {/* Reset — right side, next to the Platform filter row (Rotem). */}
        <button
          type="button"
          className="iris-btn ml-auto text-xs"
          onClick={() => onChange({})}
          aria-label="Reset filters"
        >
          Reset
        </button>
      </div>
      {aside}
    </div>
  );
}
